import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useStyletron } from 'baseui'
import { SIZE } from 'baseui/button'
import { Modal, ROLE, SIZE as MODAL_SIZE } from 'baseui/modal'
import { useTranslation } from 'react-i18next'
import SDButton from '@/shared/components/SDComponents/SDButton/SDButton'
import AbandonModalContent from '@/shared/components/contracts/AbandonModalContent'
import CommentsModalContent, { CommentItem } from '@/shared/components/contracts/CommentsModalContent'
import { selectCurrentContract } from '../../DashboardV2Selectors'
import { dashboardV2Actions } from '../../DashboardV2Sagas'
import { contractToolsActions } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Common/ContractToolsSagas'

interface ActionsCellProps {
  contractId: string
  contactZohoId: string
  statusRaw: string
  contactId: string
  getContracts: () => void
  formInitUrl: string
}

const ActionsCell = ({
  contractId,
  contactZohoId,
  statusRaw,
  contactId,
  getContracts,
  formInitUrl,
}: ActionsCellProps) => {
  const { t } = useTranslation()
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const [modalType, setModalType] = useState('')

  const currentContract = useSelector(selectCurrentContract)

  const containerStyle = css({
    display: 'flex',
    flexDirection: 'column',
    gap: '.25rem',
  })

  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation()

  const openModal = (type: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (type === 'comments') {
      const url = (formInitUrl ?? '').replace('*', contactZohoId)
      dispatch({ type: dashboardV2Actions.LOAD_CONTRACT_DETAILS, url, contactZohoId })
    }
    setModalType(type)
    setIsOpen(true)
  }

  const closeModal = () => {
    getContracts()
    setIsOpen(false)
  }

  const handleAbandonSubmit = (reason: string, callback: (success: boolean) => void) => {
    dispatch({
      type: contractToolsActions.RETRACT_CONTRACT_V2,
      reason,
      id: contractId,
      callback: (_response: unknown, status: number) => {
        const success = status === 200
        callback(success)
        if (success) closeModal()
      },
    })
  }

  const handleCommentSubmit = (comment: string, callback: (success: boolean) => void) => {
    dispatch({
      type: contractToolsActions.ADD_COMMENT_TO_CONTACT_V2,
      comment,
      contactId: contactId,
      callback: (response: unknown, status: number) => {
        const success = status === 200
        callback(success)
        if (success) closeModal()
      },
    })
  }

  const isContractLoaded = currentContract?.id === contactZohoId
  const isLoadingComments = isOpen && modalType === 'comments' && !isContractLoaded
  const comments: CommentItem[] = isContractLoaded ? ((currentContract?.comments as CommentItem[]) ?? []) : []

  return (
    <div className={containerStyle} onClick={stopPropagation}>
      {/* @ts-expect-error SDButton is a JS component */}
      <SDButton
        size={SIZE.mini}
        block
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
          window.open('/contact/' + contactZohoId + '#' + contractId, '_blank')
        }}
      >
        {t('contractsTable:edit')}
      </SDButton>

      {statusRaw !== 'R0400' && statusRaw !== 'R0500' && (
        /* @ts-expect-error SDButton is a JS component */
        <SDButton size={SIZE.mini} block onClick={(e: React.MouseEvent) => openModal('abandon', e)}>
          {t('contractsTable:abandon')}
        </SDButton>
      )}

      {/* @ts-expect-error SDButton is a JS component */}
      <SDButton size={SIZE.mini} block onClick={(e: React.MouseEvent) => openModal('comments', e)}>
        {t('contractsTable:comments')}
      </SDButton>

      <Modal
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        animate
        autoFocus
        size={MODAL_SIZE.auto}
        role={ROLE.dialog}
      >
        {modalType === 'abandon' && <AbandonModalContent onSubmit={handleAbandonSubmit} />}
        {modalType === 'comments' && (
          <CommentsModalContent
            comments={comments}
            onSubmit={handleCommentSubmit}
            isLoadingComments={isLoadingComments}
          />
        )}
      </Modal>
    </div>
  )
}

export default ActionsCell
